import {shortcutsPath} from '@github-ui/paths'
import {useMutation} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useConfirm} from '@primer/react'
import {useState} from 'react'

import type {SidebarLink, UserShortcut} from '../types'
import {isValidShortcut} from '../utils/shortcut'
import {extractResponseErrorMessage} from '../utils/shortcut-error'

export type EditableShortcutField = 'name' | 'description' | 'query' | 'icon' | 'color'

const defaultErrorMessage = 'Failed to save shortcut'

interface Params {
  type: UserShortcut['searchType']
  onShortcutSaved: (savedShortcut: SidebarLink) => void
}

/**
 * Hook for creating and editing user shortcuts (saved views). Designed to be used in conjunction with the
 * `ShortcutEditingHeader` component.
 *
 * @param type The type of shortcut being used: one of 'repositories', 'issues' or 'pull-requests'.
 * @param onShortcutSaved Callback invoked when a shortcut is successfully created or updated.
 *
 * @returns An object containing resources and methods for managing shortcut creation and editing.
 */
export function useShortcutEditor({type, onShortcutSaved}: Params) {
  const confirm = useConfirm()
  const [shortcut, setShortcut] = useState<UserShortcut | undefined>(undefined)
  const [isDirty, setIsDirty] = useState(false)

  const saveAndDismissMutation = useMutation({
    mutationFn: async (shortcutToSave: UserShortcut) => {
      const response = await verifiedFetchJSON(shortcutsPath(shortcutToSave.id), {
        method: shortcutToSave.id ? 'PUT' : 'POST',
        body: {view: shortcutToSave},
      })

      if (!response.ok) {
        throw new Error(await extractResponseErrorMessage(response, defaultErrorMessage))
      }

      const savedShortcut = (await response.json()).shortcut as UserShortcut
      return savedShortcut
    },
    onSuccess: savedShortcut => {
      onShortcutSaved({...savedShortcut, source: 'user'})
      doDismiss()
    },
  })

  /**
   * Initializes a new shortcut for creation.
   *
   * @param query The filter query for the new shortcut. Query is handled externally and passed in here to allow for
   * the query state to be consistent between the shortcut editor being open or closed.
   * @param baseShortcut An optional shortcut to copy icon and color from.
   */
  function create(query: string, baseShortcut?: SidebarLink) {
    const newShortcut: UserShortcut = {
      id: '',
      name: '',
      searchType: type,
      query,
      description: '',
      icon: 'bookmark',
      color: 'gray',
    }

    if (baseShortcut) {
      newShortcut.icon = baseShortcut.icon
      newShortcut.color = baseShortcut.color
    }

    setShortcut(newShortcut)
    setIsDirty(false)
    saveAndDismissMutation.reset()
  }

  /**
   * Initializes an existing shortcut for editing. The query state is kept consistent by passing in the current value
   * of the filter query. This means that if the user has modified the query before clicking "edit", the
   * modified query will be shown in the editor along with the other shortcut details.
   *
   * @param shortcutToEdit The shortcut to edit.
   * @param query The current filter query, passed in to keep the query state consistent.
   */
  function edit(shortcutToEdit: SidebarLink, query: string) {
    if (shortcut) {
      throw new Error('A shortcut is already being edited. Cannot edit another one simultaneously.')
    }
    setShortcut({...shortcutToEdit, query})
    setIsDirty(false)
  }

  /**
   * Dismisses the shortcut editor (by setting `shortcut` to undefined), prompting to discard unsaved changes if any.
   * This assumes that the shortcut editor component is conditionally rendered based on the presence of `shortcut`.
   */
  async function dismiss(): Promise<boolean> {
    const shouldDiscard =
      !isDirty ||
      (await confirm({
        title: 'Discard changes',
        content: `Are you sure you want to discard changes?`,
        confirmButtonContent: 'Discard',
        confirmButtonType: 'danger',
      }))

    if (!shouldDiscard) return false

    doDismiss()
    return true
  }

  function doDismiss() {
    setShortcut(undefined)
    saveAndDismissMutation.reset()
  }

  /**
   * Sets a specified field of the current shortcut.
   */
  function setValue(fieldName: EditableShortcutField, value: string) {
    setShortcut(prevShortcut => {
      if (!prevShortcut) {
        throw new Error('No shortcut is being edited currently. Updating should not be possible.')
      }
      return {...prevShortcut, [fieldName]: value}
    })
    setIsDirty(true)
  }

  /**
   * Saves the current shortcut if valid, invoking the `onShortcutSaved` callback upon success before
   * dismissing the editor.
   */
  async function saveAndDismiss() {
    if (!shortcut) {
      throw new Error('No shortcut is being edited currently. Saving should not be possible.')
    }

    if (saveAndDismissMutation.isPending) return
    if (!isValidShortcut(shortcut)) return

    saveAndDismissMutation.mutate(shortcut)
  }

  return {
    shortcut,
    setValue,
    create,
    edit,
    dismiss,
    saveAndDismiss,
    isSaving: saveAndDismissMutation.isPending,
    errorMessage: saveAndDismissMutation.error?.message,
  }
}
